import { EntityCreateResponse } from '@faro/http-clients';
import {
    CommandResponse,
    RevisionedCommandResponse,
    ScheduleOfActivities,
    toScheduleOfActivities,
} from '@faro/study-designer-model';
import axios from 'axios';
import { Identifiable } from 'faro-common-model';
import { StudyPathParameter } from '../parameter';
import { EpochConfigurationQueryParameters } from '../EpochConfiguration/EpochConfigurationClient';

export type ScheduleOfActivitiesPathParameters = StudyPathParameter;
export type ScheduleOfActivitiesQueryParameters = EpochConfigurationQueryParameters;

/**
 * Client responsible for accessing the backend entity services
 */
export const scheduleOfActivitiesClient = {
    list: async (
        request?: ScheduleOfActivitiesPathParameters & ScheduleOfActivitiesQueryParameters,
        signal?: AbortSignal
    ): Promise<ScheduleOfActivities[]> => {
        const { studyId, ...query } = request ?? {};
        return axios
            .get(`/api/study-designer/studies/${studyId}/schedule-of-activities`, {
                signal,
                params: query,
            })
            .then(response => response.data?.map(toScheduleOfActivities) ?? []);
    },
    get: async (
        request: ScheduleOfActivitiesPathParameters & Identifiable,
        signal?: AbortSignal
    ): Promise<ScheduleOfActivities> => {
        const { studyId, id } = request;
        return axios
            .get(`/api/study-designer/studies/${studyId}/schedule-of-activities/${id}`, {
                signal,
            })
            .then(response => toScheduleOfActivities(response.data));
    },
    update: async (
        entity: ScheduleOfActivities,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/schedule-of-activities/${entity.id}`, entity, {
                signal,
            })
            .then(response => response.data);
    },
    delete: async (
        entity: Identifiable & ScheduleOfActivitiesPathParameters,
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/schedule-of-activities/${entity.id}`, { signal })
            .then(response => response.data);
    },
    // TODO: when service will be removed, it should use RevisionedCommandResponse<EntityCommandCreateResponse> from @faro/study-designer-model instead of EntityCreateResponse
    clone: async (entity: Partial<ScheduleOfActivities>, signal?: AbortSignal): Promise<EntityCreateResponse> => {
        const { studyId, id } = entity;
        return axios
            .post(`/api/study-designer/studies/${studyId}/schedule-of-activities/${id}/actions/clone`, entity, {
                signal,
            })
            .then(response => ({
                id: response.data.entityId,
                revision: response.data.revision,
            }));
    },
    move: async (
        request: ScheduleOfActivitiesPathParameters &
            Identifiable & { sequenceNumber: number } & { from: number; to: number },
        signal?: AbortSignal
    ): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { studyId, id, sequenceNumber } = request;
        return axios
            .post(
                `/api/study-designer/studies/${studyId}/schedule-of-activities/${id}/actions/move`,
                { sequenceNumber },
                { signal }
            )
            .then(response => response.data);
    },
    map: toScheduleOfActivities,
};
